import { useQueryClient } from "react-query";
import { useHistory, useParams } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { Form } from "@circle-react-uikit/Form";
import { useToast } from "@circle-react-uikit/ToastV2";
import { SkeletonLoader } from "../components/common/SkeletonLoader";
import { useAttachmentsUpload } from "../hooks/useAttachmentsUpload";
import { useShowConversation } from "../hooks/useShowConversation";
import { useSubmitConversation } from "../hooks/useSubmitConversation";
import { useUpdateConversation } from "../hooks/useUpdateConversation";
import { CREATE_CONVERSATION_SCHEMA } from "../schemas";
import type { HelpWidgetConversation } from "../types/help_widget_conversation";
import { HelpWidgetLayout } from "./common/HelpWidgetLayout";
import { SendUsAMessageForm } from "./forms/SendUsAMessageForm";

export const CreateConversationView = () => {
  const { id: conversationId } = useParams<{ id: string }>();
  const toastr = useToast();
  const history = useHistory();
  const queryClient = useQueryClient();

  const { uploadAttachments, attachments, cancelAttachment } =
    useAttachmentsUpload({ conversationId });

  const { mutate: submitConversation, isLoading } = useSubmitConversation({
    onSuccess: () => {
      history.push(`/message_confirmation/${conversationId}`);
    },
    onError: () => {
      toastr.error(t("request_failure_message"));
    },
  });

  const { data: conversation, isLoading: isConversationLoading } =
    useShowConversation(conversationId) as {
      data: HelpWidgetConversation | undefined;
      isLoading: boolean;
    };

  const { mutate: updateConversation } = useUpdateConversation({
    onSuccess: () => {
      void queryClient.invalidateQueries([
        "help-widget",
        "conversations",
        conversationId,
      ]);
    },
    onError: () => {
      toastr.error(t("request_failure_message"));
    },
  });
  const upsertConversation = (type: string, text: string, priority: string) => {
    const conversation = {
      title: t("help_widget.report_bug.title"),
      data: {
        type,
        text,
        priority,
      },
      status: "draft" as const,
    };

    updateConversation({
      id: conversationId,
      conversation,
    });
  };

  const handleSubmit = (data: {
    text: string;
    type: string;
    priority: string;
  }) => {
    submitConversation({
      id: conversationId,
      conversation: {
        title: t("help_widget.report_bug.title"),
        status: "open",
        data: {
          type: data.type,
          text: data.text,
          priority: data.priority,
        },
      },
      attachment_ids: attachments.map(
        attachment => attachment.attachmentId,
      ) as string[],
    });
  };

  const onFormChange = (data: {
    text: string;
    type: string;
    priority: string;
  }) => {
    upsertConversation(data.type, data.text, data.priority);
  };

  if (isConversationLoading) {
    return (
      <div className="flex size-full flex-1 flex-col p-4">
        <SkeletonLoader rows={4} height="52px" wrapperClassName="!px-0" />
      </div>
    );
  }
  const { text, type, priority } = conversation?.data || {};
  const defaultValues = { text, type, priority };

  return (
    <HelpWidgetLayout
      id="send-message-form-container"
      className="relative flex-1 overflow-hidden"
    >
      <div className="scrollbar-hide absolute inset-0 overflow-y-auto">
        <Form
          preventAccidentalLeave
          validationSchema={CREATE_CONVERSATION_SCHEMA}
          id="send-message-form"
          onSubmit={handleSubmit}
          mode="onSubmit"
          defaultValues={defaultValues}
        >
          <SendUsAMessageForm
            isLoading={isLoading}
            attachments={attachments}
            uploadAttachments={uploadAttachments}
            cancelAttachment={cancelAttachment}
            onFormChange={onFormChange}
          />
        </Form>
      </div>
    </HelpWidgetLayout>
  );
};
